import { Camera, Trash } from '@phosphor-icons/react';
import type { LangueParlee } from '@sirocco/shared';
import { useState } from 'react';
import { users as usersApi } from '../../api/endpoints';
import { ScreenError, ScreenLoader, SubScreen } from '../../components/layout/Screens';
import { Avatar } from '../../components/ui/Avatar';
import { Button } from '../../components/ui/Button';
import { ChoixDesLangues } from '../../components/ui/ChoixDesLangues';
import { Field, TextArea } from '../../components/ui/Field';
import { PhotoPicker } from '../../components/ui/PhotoPicker';
import { useSession } from '../../store/SessionContext';
import { t } from '../../i18n';

/**
 * Avatar, pseudo, bio et langues parlees.
 *
 * La photo part des qu'elle est choisie — un avatar qu'on voit changer
 * n'a pas besoin d'etre confirme — tandis que le pseudo attend le bouton
 * d'enregistrement : il est unique, et son refus doit pouvoir s'afficher
 * a cote du champ.
 */
export function AccountScreen() {
  const { user, setUser } = useSession();

  const [username, setUsername] = useState(user?.username ?? '');
  const [bio, setBio] = useState(user?.bio ?? '');
  const [langues, setLangues] = useState<LangueParlee[]>(user?.languesParlees ?? []);
  const [notice, setNotice] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function savePhoto(avatarUrl: string | null) {
    setFailure(null);
    try {
      setUser(await usersApi.update({ avatarUrl }));
      setNotice(avatarUrl ? t.profil.compte.photoMiseAJour : t.profil.compte.photoRetiree);
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : t.profil.compte.photoImpossible);
    }
  }

  async function save() {
    setPending(true);
    setFailure(null);
    setNotice(null);
    try {
      setUser(
        await usersApi.update({
          username: username.trim(),
          bio: bio.trim(),
          languesParlees: langues,
        }),
      );
      setNotice(t.profil.compte.enregistre);
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : t.profil.compte.impossible);
    } finally {
      setPending(false);
    }
  }

  const changed = user
    ? username.trim() !== user.username ||
      bio.trim() !== user.bio ||
      langues.join() !== user.languesParlees.join()
    : false;

  return (
    <SubScreen
      title={t.profil.commun.personnaliser}
      bottomBar={
        <Button block loading={pending} disabled={!changed} onClick={() => void save()}>
          {t.profil.compte.enregistrer}
        </Button>
      }
    >
      {!user && <ScreenLoader />}
      {failure && <ScreenError message={failure} />}

      {user && (
        <>
          <div className="pr-photo">
            <PhotoPicker
              preset="avatar"
              label={t.profil.commun.changerPhoto}
              className="pr-photo__button"
              onPicked={savePhoto}
              onError={setFailure}
            >
              <Avatar user={user} size={104} ring />
              <span className="pr-photo__badge" aria-hidden="true">
                <Camera size={18} weight="fill" />
              </span>
            </PhotoPicker>

            {user.avatarUrl && (
              <button className="pr-photo__remove" onClick={() => void savePhoto(null)}>
                <Trash size={16} />
                {t.profil.compte.retirerPhoto}
              </button>
            )}
          </div>

          <Field
            tone="spot"
            label={t.profil.compte.pseudo}
            value={username}
            maxLength={24}
            autoComplete="username"
            onChange={(event) => setUsername(event.target.value)}
          />

          <TextArea
            tone="spot"
            className="pr-account__bio"
            label={t.profil.commun.bio}
            placeholder={t.profil.compte.bioExemple}
            value={bio}
            maxLength={160}
            onChange={(event) => setBio(event.target.value)}
          />

          <ChoixDesLangues
            className="pr-account__bio"
            label={t.profil.compte.langues}
            aide={t.profil.compte.languesAide}
            valeur={langues}
            onChange={setLangues}
          />

          {notice && <p className="pr-photo__notice">{notice}</p>}
        </>
      )}
    </SubScreen>
  );
}
